/**
 * THE CHANGE REQUEST AS A FILE SOMEBODY SIGNS — #195.
 *
 * WHY A PDF. Vinnie, 23 August 2026: it is the copy a client keeps, and the one
 * they cannot quietly alter afterwards. Everything else about a change request
 * is regenerated from the board each time it is opened — that is what makes a
 * wrong item line fixable. The moment it is sent for signature that has to
 * stop, and a file is how it stops.
 *
 * WHAT THIS IS SHAPED LIKE, AND WHY IT CHANGED. The first version was a plain
 * typeset page. Vinnie, 23 August: *"The form is fine, but it does not look
 * professional."* He sent CPFC's own sign-off form beside it — numbered
 * sections, each opening with a filled band, a summary table at the top, and a
 * Document Signatures table at the foot. That is what a document going to a
 * client and into a file looks like, so that is what this draws.
 *
 * THE THREE THINGS HE ASKED FOR, all of them on the same day:
 *
 *   AN UNWRITTEN SECTION IS NOT PRINTED. *"The first 3 fields if left empty
 *   should not be presented in the PDF."* And producing no longer waits for
 *   them: *"We should be able to produce the form."*
 *
 *   THE SIGNATURES ARE ON THE DOCUMENT. *"Where is the record on the PDF form
 *   that it was signed off?"* Section 6 of his own form is Title / Name /
 *   Signature / Date, and this draws the same table from the sign-off panel.
 *
 *   AND IT IS WORTH PRINTING. *"That approved PDF should be printable to show
 *   someone who approved, when and by whom."* So a name that has answered
 *   carries how they answered and the date, and one that has not is left blank
 *   — exactly as his form leaves LaToya Darmanie's and Vinai Narace's rows
 *   blank while Dominic Smart's and Richard Robinson's carry 21/08/2026.
 *
 * PLAIN HELVETICA, NO EMBEDDED FONT. One of the fourteen faces every reader
 * already has, so the file stays small and opens the same everywhere — which
 * for a document kept for years matters more than matching the app's typeface.
 */
import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { winAnsi, unwrap } from './pdf-text';
import {
  drawLetterhead,
  drawBand,
  wordedFromSlashed,
  preparedSegments,
  revisionLabel,
  TINT_LABEL,
  BRAND,
  type Letterhead,
} from './pdf-chrome';
import { embedTitleFont } from './pdf-fonts';
import type { ChangeRequest, ChangeRequestTemplate, WrittenByHand } from './change-request';
import { STANDARD_FORM } from './change-request';

/** A4 in points, which is what pdf-lib measures in. */
const PAGE = { width: 595.28, height: 841.89 };
const MARGIN = 52;
const WIDTH = PAGE.width - MARGIN * 2;

/**
 * EVERY PAGE SAYS WHAT IT IS AND WHICH PAGE IT IS — Vinnie, 23 August 2026:
 * *"If a signature page is by itself then all pages should be numbered and
 * titled so they know where it came from for audit purposes."*
 *
 * WHY THIS IS NOT DECORATION. A sign-off document does not stay stapled. The
 * signature page is the one that gets photocopied, scanned, faxed and attached
 * to something else, and a loose page carrying names and dates but nothing
 * saying WHICH document they signed is worth very little to an auditor — and is
 * exactly what somebody would produce if they wanted a signature to appear
 * against a document it was never given for. A page number with a total is the
 * other half of it: "page 2 of 4" says a page is missing, "page 2" does not.
 *
 * So: the mark and the title run across the head of every page after the first,
 * and the foot of every page carries the board, the version, and page n of m.
 * These two reserve their space during layout rather than being drawn over the
 * top afterwards, because furniture that overprints the last line of a table is
 * worse than no furniture.
 */
const FOOT = 38;
const HEAD = 36;

const INK = rgb(0.1, 0.13, 0.16);
const QUIET = rgb(0.42, 0.46, 0.5);
const RULE = rgb(0.8, 0.83, 0.85);
const SUNK = rgb(0.965, 0.972, 0.976);

/**
 * THE CUSTOMER'S MARK, if the board has one.
 *
 * PASSED IN AS BYTES rather than fetched here, for the same reason the item
 * lines are: this file lays out what it is given and reaches for nothing, so it
 * can be tested without a network and cannot quietly draw a different logo from
 * the one the caller thought it had.
 */
export interface Logo {
  bytes: ArrayBuffer | Uint8Array;
  type: 'png' | 'jpg';
}

export interface ChangeRequestFacts {
  title: string;
  board: string;
  sprint: string;
  /** Stamped in, never read from the clock here, so the same document twice is
   *  the same bytes and a test can say what the date will be. */
  raisedOn: string;
  /** Who raised it — the signed-in person, named on the letterhead just before the
   *  raised date. Stamped in at produce time, like the date. */
  raisedBy?: string;
  version: string;
  logo?: Logo;
}

/**
 * ONE ROW OF SECTION 6, and it is the sign-off panel rather than anything this
 * file decides. `signedOn` empty means they have not answered — printed blank,
 * not printed as "pending", because a blank cell in a signature table is
 * already understood by everybody who has ever seen one.
 */
export interface SignatureRow {
  title: string;
  name: string;
  /** How they answered, in words a reader who has never used FlightDeck can
   *  understand — "Approved by link", "Rejected", "Approved on behalf". */
  how: string;
  signedOn: string;
  /** The clock time they answered, to the minute. Empty when `signedOn` is —
   *  a blank cell, never "pending". Paired with the date so the record says
   *  when as well as who, which is what an audit trail is. */
  signedTime: string;
}

/** CPFC write 07/07/2026. Day first, which is what the Caribbean and the UK read. */
export const asDate = (at: number): string => {
  const d = new Date(at);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/**
 * WORDS THAT FIT, measured in the font they will be drawn in.
 *
 * Wrapping by counting characters is what makes a generated document look
 * generated — a proportional face puts twice as many i's as w's on a line. A
 * word longer than the whole measure is left to overhang rather than dropped:
 * losing a word from a signed document is worse than a ragged edge.
 */
function wrap(text: string, font: PDFFont, size: number, measure: number): string[] {
  const lines: string[] = [];
  /*
   * SANITISED HERE, BEFORE ANYTHING IS MEASURED — pdf-lib refuses an
   * unencodable character in `widthOfTextAtSize` exactly as it does in
   * `drawText`, so cleaning up at the point of drawing still crashes while
   * working out where the line breaks. See src/pdf-text.ts.
   */
  /*
   * UNWRAPPED FIRST — text pasted in was often already wrapped by something
   * else, and every one of those newlines said "paragraph ends here" to the
   * loop below. See unwrap() for how a wrap artefact is told from a list.
   */
  for (const paragraph of unwrap(winAnsi(text)).split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const tried = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(tried, size) <= measure) line = tried;
      else {
        if (line) lines.push(line);
        line = word;
      }
    }
    lines.push(line);
  }
  return lines;
}

/** What pdf-lib hands back from embedPng/embedJpg, named so it can be carried. */
type Art = Parameters<PDFPage['drawImage']>[0] & { width: number; height: number };

/** The mark and title that run across the head of every continuation page. */
interface Running {
  title: string;
  logo?: Embedded;
}

interface Embedded {
  art: Art;
  width: number;
  height: number;
}

/**
 * A LOGO THAT WILL NOT EMBED IS SKIPPED, NOT FATAL. The document is the point;
 * losing the branding is a blemish and losing the change request is a failure,
 * so a corrupt image costs the first and not the second.
 *
 * Shared by the document and the approved copy, because the approval record is
 * a page somebody prints on its own and it needs the mark for the same reason
 * every other page does.
 */
async function embed(pdf: PDFDocument, logo?: Logo): Promise<Embedded | undefined> {
  if (!logo) return undefined;
  try {
    const art =
      logo.type === 'png' ? await pdf.embedPng(logo.bytes) : await pdf.embedJpg(logo.bytes);
    return { art, width: art.width, height: art.height };
  } catch {
    /* Not an image this reader can hold. The document still is one. */
    return undefined;
  }
}

/**
 * PAGE n OF m, STAMPED AFTER THE FACT, because m is not known until the last
 * line is drawn. Every page gets the same identifying line beneath a hairline,
 * and its own number.
 *
 * `from` IS THERE FOR THE APPROVED COPY, and it is the whole reason this takes
 * a range rather than doing the document. The approved copy is the signed file
 * with a page added — the signed pages are evidence and are not redrawn, so
 * they keep the footers they were signed with, and the added pages are numbered
 * among themselves. That is why the record's label says so in words: a reader
 * holding a three-page file sees "page 1 of 2" then "record of approval — page
 * 1 of 1", which is two documents attached, and is what they are.
 */
function stampFooters(
  pdf: PDFDocument,
  from: number,
  said: string,
  label: string,
  font: PDFFont
): void {
  const pages = pdf.getPages().slice(from);
  pages.forEach((page, at) => {
    const size = 7.5;
    page.drawLine({
      start: { x: MARGIN, y: MARGIN + 22 },
      end: { x: MARGIN + WIDTH, y: MARGIN + 22 },
      thickness: 0.75,
      color: RULE,
    });
    page.drawText(winAnsi(said), { x: MARGIN, y: MARGIN + 10, size, font, color: QUIET });
    const numbered = winAnsi(`${label} ${at + 1} of ${pages.length}`);
    page.drawText(numbered, {
      x: MARGIN + WIDTH - font.widthOfTextAtSize(numbered, size),
      y: MARGIN + 10,
      size,
      font,
      color: QUIET,
    });
  });
}

/**
 * A PEN THAT KNOWS WHERE IT IS ON THE PAGE, and starts a new one when it runs
 * out. Written as a closure because every drawing function needs the same two
 * things — how far down we are and which page that is — and threading them by
 * hand is how a section ends up drawn off the bottom of a page nobody looks at.
 */
function makePen(pdf: PDFDocument, regular: PDFFont, bold: PDFFont, running?: Running, titleFont?: PDFFont) {
  let page: PDFPage = pdf.addPage([PAGE.width, PAGE.height]);
  let y = PAGE.height - MARGIN;
  let section = 0;

  /**
   * The head of a CONTINUATION page — never the first, which carries the logo
   * at full size and the document's own title and needs no reminder of itself.
   */
  const carryOver = () => {
    if (!running) return;
    const top = y;
    const tall = 13;
    let x = MARGIN;
    if (running.logo) {
      const wide = (running.logo.width / running.logo.height) * tall;
      /* Held to a third of the measure, so a banner-shaped logo cannot push the
         title off the page. */
      const scale = Math.min(1, WIDTH / 3 / wide);
      page.drawImage(running.logo.art, {
        x,
        y: top - tall * scale,
        width: wide * scale,
        height: tall * scale,
      });
      x += wide * scale + 9;
    }
    page.drawText(winAnsi(running.title), { x, y: top - tall + 3.5, size: 8.5, font: regular, color: QUIET });
    page.drawLine({
      start: { x: MARGIN, y: top - tall - 9 },
      end: { x: MARGIN + WIDTH, y: top - tall - 9 },
      thickness: 0.75,
      color: RULE,
    });
    y = top - HEAD;
  };

  const room = (needed: number) => {
    /* The floor is the footer, not the margin — see FOOT. */
    if (y - needed >= MARGIN + FOOT) return;
    page = pdf.addPage([PAGE.width, PAGE.height]);
    y = PAGE.height - MARGIN;
    carryOver();
  };

  const lines = (body: string, size: number, measure: number, font: PDFFont) =>
    wrap(body, font, size, measure);

  return {
    get pages() {
      return pdf.getPageCount();
    },
    down: (by: number) => {
      y -= by;
    },
    room,

    /** Drawn at the size the caller worked out, and the pen moves past it. */
    image(width: number, height: number, art: Parameters<PDFPage['drawImage']>[0]) {
      room(height + 10);
      y -= height;
      page.drawImage(art, { x: MARGIN, y, width, height });
      y -= 10;
    },

    text(
      body: string,
      opts: { size?: number; bold?: boolean; colour?: typeof INK; indent?: number; gap?: number } = {}
    ) {
      const size = opts.size ?? 10;
      const font = opts.bold ? bold : regular;
      const indent = opts.indent ?? 0;
      const leading = size * 1.5;
      for (const line of lines(body, size, WIDTH - indent, font)) {
        room(leading);
        y -= leading;
        page.drawText(line, { x: MARGIN + indent, y, size, font, color: opts.colour ?? INK });
      }
      if (opts.gap) y -= opts.gap;
    },

    /**
     * A NUMBERED SECTION, opening with a filled band — CPFC's own shape.
     *
     * Numbered as it is DRAWN rather than from a fixed list, because a section
     * nobody wrote is not printed at all: numbering from the template would
     * leave a document that jumps from 2 to 4 and reads as though a page is
     * missing.
     */
    band(title: string) {
      const height = 26;
      // Never the last thing on a page: a heading with its section overleaf.
      room(height + 30);
      y -= height + 8;
      section += 1;
      drawBand(page, { x: MARGIN, yBottom: y, width: WIDTH, title: winAnsi(title), number: section, bold });
      y -= 10;
    },

    /** The letterhead (R18): eyebrow, big subject, quiet meta, logo, teal hairline. */
    letterhead(content: Letterhead) {
      y = drawLetterhead(page, { x: MARGIN, width: WIDTH, top: PAGE.height - MARGIN }, { regular, bold, title: titleFont }, content);
    },

    /**
     * A BORDERED TABLE. Row height comes from the tallest cell in it, so a long
     * assumption does not print over the row beneath.
     *
     * A ROW IS NEVER SPLIT ACROSS PAGES. Half a signature row at the foot of one
     * page and half at the head of the next is the sort of thing that makes
     * somebody doubt the whole document.
     */
    table(
      widths: number[],
      rows: string[][],
      /*
       * `labels` NAMES THE COLUMNS THAT ARE LABELS, not content — #228.
       *
       * The summary table is label/value pairs side by side: Project | Dosely
       * | Version | 1. Every cell was drawn identically, so "Project" and
       * "Dosely" looked like the same kind of thing and the reader had to work
       * out which was which. Vinnie, 24 August 2026: *"Project is a title.
       * Covers? Isn't that a title as well? Titles need to be defined so that
       * you know what is the title and what the title is."*
       *
       * A label cell now gets the band colour and white text — the same
       * treatment as the section headings above it, so the document says
       * "this is a heading" in one visual language rather than two.
       */
      opts: { head?: boolean; size?: number; labels?: number[] } = {}
    ) {
      const size = opts.size ?? 9.5;
      const pad = 6;
      const leading = size * 1.35;
      const cols = widths.map((w) => w * WIDTH);

      rows.forEach((row, at) => {
        const isHead = at === 0 && opts.head;
        const isLabel = (i: number) => !isHead && !!opts.labels?.includes(i);
        /* Measured in the face each cell is actually drawn in — a label is bold
           and bold is wider, so measuring them all as regular puts the longest
           label a hair over its cell. */
        const wrapped = row.map((cell, i) =>
          lines(cell, size, cols[i] - pad * 2, isHead || isLabel(i) ? bold : regular)
        );
        const height = Math.max(...wrapped.map((w) => w.length)) * leading + pad * 2 - 2;
        room(height);
        y -= height;
        let x = MARGIN;
        wrapped.forEach((cell, i) => {
          if (isHead) page.drawRectangle({ x, y, width: cols[i], height, color: SUNK });
          else if (isLabel(i)) page.drawRectangle({ x, y, width: cols[i], height, color: TINT_LABEL });
          page.drawRectangle({
            x,
            y,
            width: cols[i],
            height,
            borderColor: RULE,
            borderWidth: 0.75,
          });
          cell.forEach((line, n) => {
            page.drawText(line, {
              x: x + pad,
              y: y + height - pad - size + 1 - n * leading,
              size,
              font: isHead || isLabel(i) ? bold : regular,
              /* White on the band, the same as the section headings. */
              color: isHead ? QUIET : isLabel(i) ? BRAND : INK,
            });
          });
          x += cols[i];
        });
      });
    },
  };
}

type Pen = ReturnType<typeof makePen>;


/**
 * THE SAME DOCUMENT PRODUCES THE SAME FILE, and that needs saying out loud.
 *
 * pdf-lib stamps the moment of saving into every file it writes, so producing
 * the identical document twice gave two different sets of bytes — which was
 * caught by a test that had been passing only because the two calls happened to
 * land in the same millisecond.
 *
 * WHY IT MATTERS FOR THIS DOCUMENT IN PARTICULAR. A change request is signed
 * against a fingerprint of the file. If the bytes depend on WHEN it was
 * produced rather than only on what it says, that fingerprint stops being
 * checkable: nobody can regenerate the document and show it is the one that was
 * signed. Dating it from the document's own raised date makes the file a
 * function of its content and nothing else.
 */
function stampDates(pdf: PDFDocument, raisedOn: string): void {
  const [day, month, year] = raisedOn.split('/').map(Number);
  const when =
    Number.isFinite(day) && Number.isFinite(month) && Number.isFinite(year)
      ? new Date(Date.UTC(year, month - 1, day))
      : new Date(Date.UTC(2000, 0, 1));
  pdf.setCreationDate(when);
  pdf.setModificationDate(when);
}

/** A bulleted item: its title, then the line the template composed. */
/**
 * A bulleted item: its title, a plain-English line of what it is, then the line
 * the template composed.
 *
 * THE SUMMARY SAYS WHAT THE WORK IS — #228, the half a signatory actually reads.
 * It prints under the title ONLY when the card has one. A card with no summary
 * prints title straight to detail, exactly as before — the produced document
 * stays clean for the customer (Vinnie, 5 Oct 2026: the prompt to add a summary
 * belongs on the editor, not on the page someone signs). A frozen older
 * document (summary `undefined`) is likewise untouched. The fuller technical
 * notes field is never carried here; see ItemLine in change-request.ts.
 */
function item(pen: Pen, title: string, detail: string, summary: string | undefined, why?: string) {
  pen.text(title, { size: 10.5, bold: true });
  if (summary) pen.text(summary, { size: 9.5, gap: 1 });
  pen.text(detail, { size: 9, colour: QUIET });
  if (why) pen.text(`Reason: ${why}`, { size: 9 });
  pen.down(5);
}

/**
 * The document, as bytes.
 *
 * TAKES WHAT IT PRINTS rather than reading the board itself. The caller has
 * already worked out what the document says — see change-request.ts — and this
 * only lays it out, so the two can be tested apart and the harder of them
 * without a PDF reader.
 */
export async function changeRequestPdf(
  facts: ChangeRequestFacts,
  request: ChangeRequest,
  written: WrittenByHand,
  template: ChangeRequestTemplate = STANDARD_FORM
): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  pdf.setTitle(facts.title);
  pdf.setSubject(`Change request — ${facts.board}`);
  pdf.setProducer('FlightDeck');
  stampDates(pdf, facts.raisedOn);

  /*
   * THE LOGO SITS ABOVE THE TITLE, at whatever height keeps its own proportions.
   *
   * SCALED TO A HEIGHT, NOT A BOX. A logo squeezed into a fixed rectangle comes
   * out stretched, and a stretched logo on a document going to the company that
   * owns it is worse than no logo at all. A wide one is held to the page width
   * instead, so a banner and a square mark both come out right.
   *
   * A LOGO THAT WILL NOT EMBED IS SKIPPED, NOT FATAL. The document is the point;
   * losing the branding is a blemish and losing the change request is a
   * failure, so a corrupt image costs the first and not the second.
   */
  const art = await embed(pdf, facts.logo);

  /* The pen is made after the logo so the running header can carry it too. */
  const titleFont = await embedTitleFont(pdf);
  const pen = makePen(pdf, regular, bold, { title: facts.title, logo: art }, titleFont);
  pen.letterhead({
    eyebrow: 'Change request',
    subject: facts.title,
    meta: [facts.board, revisionLabel(facts.version), ...preparedSegments(facts.raisedBy, facts.raisedOn, 'Raised')]
      .filter(Boolean)
      .join('  \u00b7  '),
    logo: art?.art,
  });

  /* ── 1. Summary ───────────────────────────────────────────────────────── */
  pen.band('Summary');
  // The revision is named only once the change request has been re-issued; a first
  // issue carries no "Version 1" cell. See revisionLabel in pdf-chrome.
  const revised = Number(facts.version) > 1;
  pen.table(
    [0.22, 0.28, 0.22, 0.28],
    revised
      ? [
          ['Project', facts.board, 'Revision', String(facts.version)],
          ['Covers', facts.sprint, 'Raised', wordedFromSlashed(facts.raisedOn)],
        ]
      : [
          ['Project', facts.board, 'Covers', facts.sprint],
          ['Raised', wordedFromSlashed(facts.raisedOn)],
        ],
    { labels: [0, 2] }
  );
  pen.down(4);

  /*
   * ── The parts a person writes, and only the ones they wrote ────────────
   *
   * Vinnie, 23 August 2026: *"The first 3 fields if left empty should not be
   * presented in the PDF."* A heading over nothing reads as a section somebody
   * forgot, which is worse in a signed document than the section being absent.
   */
  for (const section of template.narratives) {
    const said = (written.narratives[section.key] ?? '').trim();
    if (!said) continue;
    pen.band(section.heading);
    pen.text(said);
    pen.down(4);
  }

  /* ── What is being asked for ──────────────────────────────────────────── */
  if (request.sections.length > 0) {
    pen.band('Items Requested');
    for (const section of request.sections) {
      pen.text(section.heading, { size: 10, bold: true, colour: BRAND });
      pen.down(3);
      for (const line of section.items) item(pen, line.title, line.detail, line.summary);
      pen.down(3);
    }
  }

  if (request.deferred.length > 0) {
    pen.band(template.deferredHeading);
    for (const line of request.deferred) item(pen, line.title, line.detail, line.summary, line.why);
  }

  /* ── The negotiation record ───────────────────────────────────────────── */
  const assumptions = written.assumptions.filter((a) => a.text.trim());
  if (assumptions.length > 0) {
    pen.band('Assumptions & Exceptions');
    pen.table(
      [0.68, 0.32],
      [
        ['Assumption', 'Business decision'],
        ...assumptions.map((a) => [a.text.trim(), a.decision]),
      ],
      { head: true }
    );
  }

  /*
   * ── NO SIGNATURE BLOCK ON THE DOCUMENT ITSELF — Vinnie, 7 September 2026 ────
   *
   * It used to draw a "Document Signatures" table here, on the argument that a
   * sign-off form needs one. But the document is produced as a DRAFT, before
   * anyone is asked, so the table was always blank and filled from whoever the
   * board would ask — which on a slots board is not even the right people. Then
   * the approved copy (below) adds a second table with the REAL signatures, and
   * a reader gets two tables that disagree. The approved copy is the record now;
   * the document carries no blank block that a card of the wrong names can fill.
   */
  stampFooters(
    pdf,
    0,
    [facts.board, revisionLabel(facts.version), `Raised ${wordedFromSlashed(facts.raisedOn)}`]
      .filter(Boolean)
      .join('  \u00b7  '),
    'Page',
    regular
  );
  return pdf.save();
}

/**
 * THE APPROVED COPY — the document exactly as it was signed, with a page added
 * saying who approved it, when, and how.
 *
 * WHY IT APPENDS RATHER THAN REGENERATES. Vinnie, 23 August 2026: *"What is the
 * point of producing the pdf to send to someone if there are no recorded
 * approvals, that approved PDF should be printable to show someone who
 * approved, when and by whom."*
 *
 * The obvious way to answer that is to generate the document again with the
 * signatures filled in. It is also wrong, and quietly: item lines come from the
 * board, the board moves, and a document regenerated a month later would say
 * something different from the one those people actually put their names to.
 * The signed file is evidence and must not be rebuilt — so this takes the file
 * as it is and adds a page to the end of it.
 *
 * THE FINGERPRINT IS ON THE PAGE for the same reason it is stored: it names
 * WHICH file was signed, so an approval record and a document can be checked
 * against each other by somebody who has only the paper.
 */
export async function approvedCopy(
  signedFile: ArrayBuffer | Uint8Array,
  facts: ChangeRequestFacts & { fileHash?: string },
  signatures: SignatureRow[]
): Promise<Uint8Array> {
  const pdf = await PDFDocument.load(signedFile);
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  /*
   * WHERE THE SIGNED DOCUMENT ENDS. Everything from here on is the record, and
   * it is the only part this numbers: the pages above were signed with the
   * footers they were signed with, and redrawing them would be altering
   * evidence to tidy it.
   */
  const record = pdf.getPageCount();
  const art = await embed(pdf, facts.logo);
  const titleFont = await embedTitleFont(pdf);
  const pen = makePen(
    pdf,
    regular,
    bold,
    {
      title: `Record of Approval \u2014 ${facts.title}`,
      logo: art,
    },
    titleFont,
  );
  pen.letterhead({
    eyebrow: 'Record of approval',
    subject: facts.title,
    meta: [facts.board, revisionLabel(facts.version)].filter(Boolean).join('  \u00b7  '),
    logo: art?.art,
  });

  pen.band('Document Signatures');
  if (signatures.length === 0) {
    pen.text('Nobody has been asked to sign this.', { size: 9.5, colour: QUIET });
  } else {
    /*
     * DATE AND TIME SHARE ONE COLUMN — Vinnie, 11 September 2026: the time is not
     * its own field; the date and the time sit in the same box, "11/09/2026 ·
     * 09:14". That is how the in-app signature table and the Sign-offs screen
     * already read it (asDate · asTime), so the record on paper matches the
     * record on screen. A row nobody has answered leaves the cell blank — the
     * join drops the empty halves rather than printing a lone separator.
     */
    pen.table(
      [0.26, 0.24, 0.24, 0.26],
      [
        ['Title', 'Name', 'Signature', 'Date & time'],
        ...signatures.map((s) => [
          s.title,
          s.name,
          s.how,
          [s.signedOn, s.signedTime].filter(Boolean).join(' · '),
        ]),
      ],
      { head: true }
    );
  }

  pen.down(10);
  pen.text(
    'These approvals were given in FlightDeck. Each person answered for themselves, or was recorded as answering on behalf with a reason, and the date and time shown are when they answered.',
    { size: 8.5, colour: QUIET }
  );
  if (facts.fileHash) {
    pen.down(4);
    pen.text(`Document fingerprint (SHA-256): ${facts.fileHash}`, { size: 7.5, colour: QUIET });
  }

  stampFooters(
    pdf,
    record,
    [facts.board, revisionLabel(facts.version), facts.title].filter(Boolean).join('  \u00b7  '),
    'Record of approval \u2014 page',
    regular
  );
  return pdf.save();
}
